import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Cookie, Shield, X } from "lucide-react";

const COOKIE_CONSENT_KEY = "connected_cookie_consent";
const COOKIE_PREFERENCES_KEY = "connected_cookie_preferences";

export default function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    personalization: false
  });

  useEffect(() => {
    // Skip banner on public/anonymous routes — parents on a public form
    // (aftercare, registration, tour booking, kiosk, password reset) shouldn't
    // have to dismiss a consent overlay before reaching the primary CTA.
    const path = window.location.pathname;
    const isPublicRoute =
      path.startsWith("/aftercare/") ||
      path.startsWith("/register/") ||
      path.startsWith("/register-v2/") ||
      path.startsWith("/book-tour/") ||
      path.startsWith("/kiosk/") ||
      path.startsWith("/r/") ||
      path.startsWith("/reset-password") ||
      path.startsWith("/setup-account/") ||
      /^\/[^/]+\/[^/]+\/book-tour$/.test(path);
    if (isPublicRoute) return;

    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleAcceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      personalization: false
    };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      timestamp: new Date().toISOString(),
      version: "1.0"
    }));
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    
    // Dispatch event for other components to react to consent change
    window.dispatchEvent(new CustomEvent("cookieConsentChanged", { detail: prefs }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/20 z-[9998]" />

      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
        <Card className="max-w-4xl mx-auto shadow-2xl border-0">
          <CardContent className="p-6">
            {showPreferences ? (
              // Preferences Panel
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <Cookie className="w-5 h-5 text-amber-500" />
                    Cookie Preferences
                  </h3>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setShowPreferences(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Essential */}
                  <div className="flex items-center justify-between py-3 border-b">
                    <div>
                      <p className="font-medium text-slate-800">Essential Cookies</p>
                      <p className="text-sm text-slate-500">
                        Required for the website to function. Cannot be disabled.
                      </p>
                    </div>
                    <Switch checked={true} disabled />
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between py-3 border-b">
                    <div>
                      <p className="font-medium text-slate-800">Analytics Cookies</p>
                      <p className="text-sm text-slate-500">
                        Help us understand how visitors interact with our website.
                      </p>
                    </div>
                    <Switch 
                      checked={preferences.analytics}
                      onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})}
                    />
                  </div>

                  {/* Marketing */}
                  <div className="flex items-center justify-between py-3 border-b">
                    <div>
                      <p className="font-medium text-slate-800">Marketing Cookies</p>
                      <p className="text-sm text-slate-500">
                        Used to track visitors across websites for advertising purposes.
                      </p>
                    </div>
                    <Switch 
                      checked={preferences.marketing}
                      onCheckedChange={(checked) => setPreferences({...preferences, marketing: checked})}
                    />
                  </div>

                  {/* Personalization */}
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-medium text-slate-800">Personalization Cookies</p>
                      <p className="text-sm text-slate-500">
                        Enable personalized features and recommendations.
                      </p>
                    </div>
                    <Switch 
                      checked={preferences.personalization}
                      onCheckedChange={(checked) => setPreferences({...preferences, personalization: checked})}
                    />
                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={handleAcceptEssential}>
                    Essential Only
                  </Button>
                  <Button onClick={handleSavePreferences}>
                    Save Preferences
                  </Button>
                </div>
              </div>
            ) : (
              // Main Banner
              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-amber-100 rounded-full shrink-0">
                    <Cookie className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">
                      We value your privacy
                    </h3>
                    <p className="text-slate-600 text-sm">
                      We use cookies to enhance your browsing experience, serve personalized content, 
                      and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. 
                      Read our{" "}
                      <a href="/privacy" className="text-sage-600 hover:underline">Privacy Policy</a>
                      {" "}and{" "}
                      <a href="/cookies" className="text-sage-600 hover:underline">Cookie Policy</a>
                      {" "}for more information.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowPreferences(true)}
                    className="order-3 sm:order-1"
                  >
                    Customize
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleAcceptEssential}
                    className="order-2"
                  >
                    Essential Only
                  </Button>
                  <Button 
                    onClick={handleAcceptAll}
                    className="order-1 sm:order-3 bg-sage-600 hover:bg-sage-700"
                  >
                    Accept All
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

// Hook to check cookie consent
export function useCookieConsent() {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const loadConsent = () => {
      const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (prefs) {
        setConsent(JSON.parse(prefs));
      }
    };

    loadConsent();

    // Listen for consent changes
    const handleConsentChange = (e) => {
      setConsent(e.detail);
    };

    window.addEventListener("cookieConsentChanged", handleConsentChange);
    return () => window.removeEventListener("cookieConsentChanged", handleConsentChange);
  }, []);

  return consent;
}
